import { Link } from "react-router-dom";
import { ArrowRight, Award, BookOpenCheck, Building2, GraduationCap, Trophy, Sparkles, ShieldCheck, CheckCircle2, Clock, Users, Briefcase, DollarSign } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const PATHWAY = [
  {
    step: "01",
    when: "Day 7 – 14",
    name: "Direct Hire PRC",
    full: "Payroll Readiness Certificate",
    issuer: "Direct Hire Team",
    cost: "Free · We cover it",
    desc: "Complete our 7 or 14-day daily-questions enrollment program and earn the Direct Hire PRC. Built to prove you're job-ready for entry-level payroll & office support roles.",
    icon: GraduationCap,
    accent: "#10B981",
    stage: "Internal · Career start",
  },
  {
    step: "02",
    when: "Month 3",
    name: "FPC",
    full: "Fundamental Payroll Certification",
    issuer: "PayrollOrg (American Payroll Association)",
    cost: "$395 · Fully sponsored by us",
    desc: "After 3 months in your placement, we cover your FPC exam fee and study materials. Industry-recognized — instantly elevates your resume for any payroll role.",
    icon: BookOpenCheck,
    accent: "#D4AF37",
    stage: "Industry-recognized",
  },
  {
    step: "03",
    when: "Year 1",
    name: "NACPB CPS",
    full: "Certified Payroll Specialist",
    issuer: "National Association of Certified Public Bookkeepers",
    cost: "$400 · Fully sponsored by us",
    desc: "After one year, we sponsor your CPS credential. Opens doors to bookkeeping + payroll hybrid roles and small-business client work.",
    icon: Award,
    accent: "#10B981",
    stage: "Mid-career credential",
  },
  {
    step: "04",
    when: "Year 3",
    name: "CPP",
    full: "Certified Payroll Professional",
    issuer: "PayrollOrg (American Payroll Association)",
    cost: "$520+ · Fully sponsored by us",
    desc: "The gold standard. After three years on the job, we fund your CPP exam, prep, and renewal — the credential that unlocks senior payroll leadership roles.",
    icon: Trophy,
    accent: "#D4AF37",
    stage: "Senior · Gold standard",
  },
];

const FAQS = [
  {
    q: "What is the Direct Hire PRC?",
    a: "The Direct Hire Payroll Readiness Certificate (PRC) is our internal completion certificate, awarded after you finish our 7-day or 14-day daily-question enrollment program. It signals to employers in our network that you've been pre-screened for attention to detail, reliability, and basic numeracy — the foundations of payroll work.",
  },
  {
    q: "Is the PRC the same as an industry certification?",
    a: "No — and we're transparent about that. The PRC is a Direct Hire Team completion credential. Industry-recognized certifications are FPC, NACPB CPS, and CPP — and we sponsor all three for graduates who stay placed with us.",
  },
  {
    q: "What's the difference between the 7-day and 14-day program?",
    a: "The 7-day track is for candidates with strong office/admin experience who already feel comfortable with Excel and numbers. The 14-day track is the friendly path for those completely new to payroll — same outcome, just more time for the concepts to land.",
  },
  {
    q: "Do I really pay nothing for the certifications?",
    a: "Correct — we cover the FPC ($395), CPS ($400), and CPP ($520+) exam fees plus study materials. Sponsorship is tied to staying placed through Direct Hire Team and meeting performance milestones. Full terms are in our Career Pathway Agreement.",
  },
  {
    q: "What happens if I leave my placement early?",
    a: "We reserve sponsorship for committed candidates. If you leave a placement within 6 months of receiving certification reimbursement, the cost is recovered per our Career Pathway Agreement. We're investing in your career — we ask the same in return.",
  },
  {
    q: "How do employers verify my PRC?",
    a: "Every PRC includes a unique 8-digit verification ID and QR code linking to a public verification page on our site, so any employer can instantly confirm it's genuine.",
  },
  {
    q: "When does the next cohort start?",
    a: "We run new cohorts monthly. Your application form will show the next available start date and remaining seats. Cohorts are capped at 24 candidates to keep onboarding personal.",
  },
];

export default function CareerPathway() {
  return (
    <div className="bg-white">
      {/* HERO */}
      <section className="relative pt-36 pb-24 bg-navy text-white overflow-hidden" data-testid="pathway-hero">
        <div className="absolute inset-0 hero-grid opacity-25" />
        <div className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-[#10B981]/15 blur-[120px]" />
        <div className="absolute -bottom-40 -left-40 h-[420px] w-[420px] rounded-full bg-[#D4AF37]/12 blur-[120px]" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3">
            <span className="h-px w-10 bg-[#D4AF37]" />
            <span className="eyebrow text-[#D4AF37]">Career Pathway · Fully Sponsored</span>
          </div>

          <h1 className="mt-6 font-serif text-5xl md:text-7xl lg:text-[5.5rem] leading-[0.98] tracking-tight max-w-5xl">
            From your first day<br />to your <em className="text-[#D4AF37] not-italic">CPP</em> —<br />we pay the whole way.
          </h1>

          <p className="mt-8 text-white/85 text-lg md:text-xl leading-relaxed max-w-2xl">
            The only payroll staffing firm that pays for your career. Earn our
            <strong className="text-white"> Payroll Readiness Certificate (PRC)</strong> in 7-14 days,
            then we fund every industry credential along the way to senior payroll leadership.
          </p>

          <div className="mt-12 flex flex-col sm:flex-row gap-4">
            <Link to="/for-candidates#apply" className="btn-gold" data-testid="pathway-hero-apply-btn">
              Reserve Your Seat <ArrowRight className="h-4 w-4" />
            </Link>
            <a href="#ladder" className="btn-ghost !border-white/20 !text-white hover:!bg-white hover:!text-navy" data-testid="pathway-hero-explore-btn">
              See the Ladder
            </a>
          </div>

          {/* Investment band */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl">
            {[
              { k: "$1,300+", v: "Invested per graduate" },
              { k: "4 certs", v: "PRC + 3 industry" },
              { k: "7–14 days", v: "To your first cert" },
              { k: "0 cost", v: "To you, ever" },
            ].map((s) => (
              <div key={s.k} className="rounded-xl border border-white/10 bg-white/5 p-5 backdrop-blur-sm" data-testid={`pathway-stat-${s.k}`}>
                <div className="font-serif text-3xl text-white">{s.k}</div>
                <div className="mt-1 text-xs uppercase tracking-widest text-white/55">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THE LADDER */}
      <section className="py-28 bg-ivory" id="ladder" data-testid="pathway-ladder-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-3xl">
            <div className="eyebrow text-[#10B981]">The Ladder</div>
            <h2 className="mt-4 font-serif text-4xl md:text-6xl text-navy leading-[1.05] tracking-tight">
              Four certificates.<br />One funded journey.
            </h2>
            <p className="mt-5 text-navy/65 text-lg max-w-2xl">
              You bring the work ethic. We bring the credentials. Every step on this ladder is paid for —
              before you ever see a bill.
            </p>
          </div>

          <div className="mt-16 space-y-4">
            {PATHWAY.map((p, idx) => (
              <div key={p.step} className="relative card-lux p-8 md:p-10 group" data-testid={`pathway-step-${p.step}`}>
                {/* Connector line */}
                {idx < PATHWAY.length - 1 && (
                  <div className="absolute left-[3.25rem] -bottom-4 h-4 w-px bg-navy/15" />
                )}
                <div className="grid md:grid-cols-12 gap-6 items-center">
                  <div className="md:col-span-1">
                    <div
                      className="h-14 w-14 rounded-full flex items-center justify-center ring-4 ring-white"
                      style={{ background: p.accent }}
                    >
                      <p.icon className="h-6 w-6 text-white" strokeWidth={2} />
                    </div>
                  </div>
                  <div className="md:col-span-7">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-mono text-xs tracking-[0.3em] text-navy/40">STEP {p.step} · {p.when.toUpperCase()}</span>
                      <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-semibold" style={{ background: `${p.accent}1A`, color: p.accent }}>
                        {p.stage}
                      </span>
                    </div>
                    <h3 className="mt-3 font-serif text-3xl md:text-4xl text-navy leading-tight">
                      {p.name} <span className="text-navy/50 font-normal text-2xl md:text-3xl">— {p.full}</span>
                    </h3>
                    <p className="mt-2 text-sm text-navy/55 italic">Issued by {p.issuer}</p>
                    <p className="mt-4 text-navy/70 leading-relaxed">{p.desc}</p>
                  </div>
                  <div className="md:col-span-4">
                    <div className="rounded-xl border p-5" style={{ borderColor: `${p.accent}33`, background: `${p.accent}08` }}>
                      <div className="eyebrow text-[10px]" style={{ color: p.accent }}>Cost to You</div>
                      <div className="mt-2 font-serif text-xl text-navy">{p.cost}</div>
                      <div className="mt-4 flex items-center gap-2 text-xs text-navy/60">
                        <Clock className="h-3.5 w-3.5" />
                        Eligibility: {p.when}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRC SPOTLIGHT */}
      <section className="py-28 bg-navy text-white relative overflow-hidden" data-testid="prc-spotlight">
        <div className="absolute inset-0 hero-grid opacity-20" />
        <div className="absolute top-1/2 -right-40 h-[420px] w-[420px] rounded-full bg-[#D4AF37]/12 blur-[120px]" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-16 items-center">
          <div className="md:col-span-6">
            <div className="eyebrow text-[#D4AF37]">Your First Win</div>
            <h2 className="mt-4 font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight">
              The Direct Hire <em className="text-[#D4AF37] not-italic">PRC</em>.
            </h2>
            <p className="mt-6 text-white/80 text-lg leading-relaxed max-w-xl">
              The Payroll Readiness Certificate is our completion credential — earned through a structured
              7-day or 14-day daily-question program. It's how we tell employers in our network: <em>this candidate is ready</em>.
            </p>

            <div className="mt-10 space-y-4">
              {[
                { i: CheckCircle2, t: "Daily-question structure", d: "10-15 min per day · designed for working adults" },
                { i: ShieldCheck, t: "Verifiable", d: "Unique ID + QR code · employers verify in seconds" },
                { i: Sparkles, t: "Recognized in our employer network", d: "PRC graduates get first look at exclusive openings" },
                { i: Briefcase, t: "Pathway to small-business roles", d: "Most graduates land their first payroll-assistant role within 30 days" },
              ].map((f, i) => (
                <div key={i} className="flex items-start gap-3">
                  <f.i className="h-5 w-5 text-[#10B981] mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-white">{f.t}</div>
                    <div className="text-sm text-white/60">{f.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Certificate visual mockup */}
          <div className="md:col-span-6">
            <div className="relative aspect-[1.4/1] rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10 transform md:rotate-[-1.5deg] hover:rotate-0 transition-transform duration-700"
              style={{
                background: "linear-gradient(135deg, #FFFEF7 0%, #F8F5E8 100%)",
              }}
            >
              {/* Border ornament */}
              <div className="absolute inset-4 border-2 rounded-xl" style={{ borderColor: "#D4AF37" }} />
              <div className="absolute inset-6 border border-dashed rounded-lg" style={{ borderColor: "#D4AF3766" }} />

              <div className="relative h-full flex flex-col items-center justify-center text-center p-10 md:p-14">
                <div className="h-12 w-12 rounded-md bg-[#0A2540] flex items-center justify-center">
                  <ShieldCheck className="h-6 w-6 text-[#D4AF37]" />
                </div>
                <div className="mt-4 eyebrow text-[10px] tracking-[0.3em]" style={{ color: "#0A2540" }}>
                  DIRECT HIRE TEAM · ESTABLISHED 2018
                </div>
                <div className="mt-6 font-serif text-2xl md:text-3xl" style={{ color: "#0A2540" }}>
                  Payroll Readiness Certificate
                </div>
                <div className="mt-1 font-mono text-xs tracking-[0.4em]" style={{ color: "#10B981" }}>
                  P · R · C
                </div>
                <div className="mt-8 text-sm italic" style={{ color: "#0A2540AA" }}>
                  This certifies that
                </div>
                <div className="mt-1 font-serif text-3xl md:text-4xl tracking-tight" style={{ color: "#0A2540" }}>
                  &lt; Your Name &gt;
                </div>
                <div className="mt-4 max-w-sm text-xs leading-relaxed" style={{ color: "#0A2540BB" }}>
                  has successfully completed the Direct Hire Team enrollment program and is recognized as
                  job-ready for entry-level payroll &amp; office support roles.
                </div>
                <div className="mt-8 grid grid-cols-2 gap-8 w-full max-w-sm text-[10px] tracking-widest uppercase" style={{ color: "#0A254088" }}>
                  <div>
                    <div className="font-mono">VERIFY ID</div>
                    <div className="mt-1 font-mono text-[#10B981]">PRC-A4F2-9X7Q</div>
                  </div>
                  <div>
                    <div className="font-mono">ISSUED</div>
                    <div className="mt-1 font-mono text-[#0A2540]">YYYY · MM · DD</div>
                  </div>
                </div>
              </div>

              {/* Gold ribbon corner */}
              <div className="absolute top-0 right-0 h-24 w-24 overflow-hidden">
                <div className="absolute -right-12 top-6 rotate-45 bg-[#D4AF37] px-12 py-1 text-[10px] tracking-widest text-[#0A2540] font-bold shadow-md">
                  CERTIFIED
                </div>
              </div>
            </div>
            <p className="mt-4 text-center text-xs text-white/45">Sample · actual certificates personalized with your name &amp; verification ID</p>
          </div>
        </div>
      </section>

      {/* WHY THIS PATHWAY */}
      <section className="py-28 bg-white" data-testid="pathway-why">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-3xl">
            <div className="eyebrow text-[#10B981]">Why It Works</div>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl text-navy leading-[1.05]">
              We invest in you because invested people stay.
            </h2>
            <p className="mt-5 text-navy/65 text-lg max-w-2xl">
              Most staffing firms place you and disappear. We sponsor you all the way to the gold standard
              of payroll — because your career growth is our retention strategy.
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {[
              { i: DollarSign, t: "$1,300+ funded per graduate", d: "FPC, CPS, and CPP exam fees plus study materials. We don't claw it back if you stay placed with us." },
              { i: Users, t: "Cohorts capped at 24 seats", d: "Keeps your enrollment experience personal — daily check-ins, real mentor time, no fire-hose." },
              { i: Building2, t: "Pre-screened employer network", d: "PRC graduates see exclusive openings before they hit any public job board." },
            ].map((c, idx) => (
              <div key={idx} className="card-lux" data-testid={`why-${idx}`}>
                <c.i className="h-6 w-6 text-[#10B981]" strokeWidth={1.8} />
                <h4 className="mt-5 font-serif text-2xl text-navy">{c.t}</h4>
                <p className="mt-3 text-navy/65 leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COHORT SCARCITY */}
      <section className="py-24 bg-ivory" data-testid="pathway-cohort">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <div className="eyebrow text-[#10B981]">Next Cohort</div>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl text-navy leading-tight">
            New cohorts every month. Seats fill fast.
          </h2>
          <p className="mt-5 text-navy/65 text-lg max-w-2xl mx-auto">
            Each cohort is capped at 24 candidates so the daily check-ins stay personal. Reserve your spot
            with a 90-second application — no commitment until you're accepted.
          </p>
          <div className="mt-10 inline-flex flex-wrap items-center justify-center gap-4 rounded-2xl border border-[#10B981]/25 bg-white px-8 py-6">
            <div className="text-left">
              <div className="eyebrow text-[10px] text-[#10B981]">Next Start</div>
              <div className="mt-1 font-serif text-2xl text-navy">Rolling weekly</div>
            </div>
            <div className="hidden md:block h-12 w-px bg-navy/10" />
            <div className="text-left">
              <div className="eyebrow text-[10px] text-[#10B981]">Format</div>
              <div className="mt-1 font-serif text-2xl text-navy">7 or 14 days</div>
            </div>
            <div className="hidden md:block h-12 w-px bg-navy/10" />
            <div className="text-left">
              <div className="eyebrow text-[10px] text-[#10B981]">Time/day</div>
              <div className="mt-1 font-serif text-2xl text-navy">10–15 min</div>
            </div>
            <Link to="/for-candidates#apply" className="btn-gold" data-testid="pathway-cohort-apply">
              Reserve a Seat <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-28 bg-white" data-testid="pathway-faq-section">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="text-center">
            <div className="eyebrow text-[#10B981]">FAQ</div>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl text-navy leading-tight">
              The honest answers.
            </h2>
          </div>
          <Accordion type="single" collapsible className="mt-12 space-y-3" data-testid="pathway-faq">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="rounded-xl border border-navy/10 bg-white px-6">
                <AccordionTrigger className="font-serif text-lg md:text-xl text-navy hover:no-underline text-left">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-navy/70 leading-relaxed pb-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* DUAL CTA */}
      <section className="py-28 bg-navy text-white relative overflow-hidden" data-testid="pathway-final-cta">
        <div className="absolute inset-0 hero-grid opacity-20" />
        <div className="absolute -top-40 -left-40 h-[420px] w-[420px] rounded-full bg-[#10B981]/12 blur-[120px]" />
        <div className="absolute -bottom-40 -right-40 h-[420px] w-[420px] rounded-full bg-[#D4AF37]/12 blur-[120px]" />

        <div className="relative max-w-5xl mx-auto px-6 md:px-10 text-center">
          <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight">
            Your career has a pathway.<br />
            <em className="text-[#D4AF37] not-italic">We'll walk it with you.</em>
          </h2>
          <p className="mt-6 text-white/75 text-lg max-w-2xl mx-auto">
            Apply today. Start your 7 or 14-day program this week. Earn your PRC, get placed, and keep
            climbing — fully sponsored, every step.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/for-candidates#apply" className="btn-gold" data-testid="pathway-final-apply">
              Start Your Career <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/contact" className="btn-ghost !border-white/20 !text-white hover:!bg-white hover:!text-navy" data-testid="pathway-final-contact">
              Have questions? Talk to us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
